// team-averages.js - team and age-group averages for a physical test, as a
// pure module. No DOM, no network; Vitest covers it directly.
//
// WHY THIS EXISTS. A test result used to have to belong to one child, which
// made every test as expensive to collect as a full per-player session. The
// director asked for two cheaper ways to record the same test (30 Oct 2026),
// so a result now arrives in one of three record modes:
//
//   'player'   one child, scored and tracked as always.
//   'anon'     one real individual time, attached to the TEAM, not to a child.
//   'average'  the team average itself, typed in, over subject_count children.
//
// All three answer the same question - how is this team doing at this test -
// so this module turns any mixture of them into one comparable number, and
// says which evidence it used.
//
// ONE BASIS AT A TIME, NEVER A BLEND. A team can hold rows in more than one
// mode (a proper session in September, a quick unattributed one in March, a
// typed average from a coach in a hurry). Averaging across modes would double
// count the same children and average an average, so the team figure rests on
// exactly ONE basis: whichever covers the most children, preferring the richer
// mode on a tie (player, then anon, then average). The basis is returned, so a
// screen can always say what the number is built on.
//
// A CAVEAT WORTH KNOWING. Mode 'player' averages each child's BEST attempt,
// because attempts are identifiable. Mode 'anon' has no identity to group by,
// so it averages every time recorded. Those are not the same statistic: if a
// team does three attempts each anonymously, their average reads slower than
// the same team's average of bests. Record one time per child in 'anon' mode
// and the two agree. The screens say so; the maths cannot fix it.

import { bestResult } from './baseline-data.js';

/** Richer first. Used only to break a tie on subject count. */
const BASIS_RANK = { player: 3, anon: 2, average: 1 };

function mean(xs) {
  return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null;
}

/** Population standard deviation, or null below two values. */
function stdev(xs) {
  if (xs.length < 2) return null;
  const m = mean(xs);
  return Math.sqrt(xs.reduce((s, x) => s + (x - m) * (x - m), 0) / xs.length);
}

function numeric(v) {
  const n = Number(v);
  return isFinite(n) ? n : null;
}

/**
 * Team-level stats for ONE exercise.
 *
 *   results      - baseline_results rows for this exercise (any mix of modes,
 *                  any number of sessions). Rows for other exercises are
 *                  ignored, so a whole season can be passed in.
 *   exercise     - the catalogue row, for its `better` direction. A missing or
 *                  directionless exercise means best/worst cannot be told
 *                  apart, so they come back null rather than guessed.
 *   teamByPlayer - player_id -> team_id, needed only to group mode-'player'
 *                  rows. Players absent from it are skipped.
 *
 * Returns Map team_id -> { teamId, basis, mean, n, best, worst, spread }
 *   n      - how many CHILDREN the figure rests on (not how many rows).
 *   best   - the best single value, direction-aware. Null on an 'average'
 *            basis, which has no individual values.
 *   spread - population SD of the individual values; null when there are
 *            fewer than two, and always null on an 'average' basis.
 */
export function teamStatsForExercise({ exerciseKey, exercise, results = [], teamByPlayer = {} } = {}) {
  const key = exerciseKey || exercise?.key || null;
  const rows = (results || []).filter(r =>
    r && (key == null || r.exercise_key === key) && numeric(r.value) != null);

  // Group the three modes separately. Nothing is combined across them.
  const perPlayer = {};   // teamId -> { playerId -> [rows] }
  const anon = {};        // teamId -> [values]
  const averages = {};    // teamId -> [{ value, count }]

  rows.forEach(r => {
    const mode = r.record_mode || 'player';
    if (mode === 'player') {
      // An external guest has no team, and is nobody's squad member.
      if (!r.player_id) return;
      const teamId = teamByPlayer[r.player_id];
      if (!teamId) return;
      ((perPlayer[teamId] ||= {})[r.player_id] ||= []).push(r);
    } else if (mode === 'anon') {
      if (!r.team_id) return;
      (anon[r.team_id] ||= []).push(numeric(r.value));
    } else if (mode === 'average') {
      if (!r.team_id) return;
      const count = Number(r.subject_count);
      (averages[r.team_id] ||= []).push({
        value: numeric(r.value),
        count: isFinite(count) && count > 0 ? count : 1,
      });
    }
  });

  const better = exercise?.better || null;
  const pickBest = (xs) => {
    if (!xs.length || !better) return null;
    return better === 'min' ? Math.min(...xs) : Math.max(...xs);
  };
  const pickWorst = (xs) => {
    if (!xs.length || !better) return null;
    return better === 'min' ? Math.max(...xs) : Math.min(...xs);
  };

  const fromValues = (teamId, basis, values) => ({
    teamId, basis,
    mean: mean(values),
    n: values.length,
    best: pickBest(values),
    worst: pickWorst(values),
    spread: stdev(values),
  });

  const out = new Map();
  const teamIds = new Set([
    ...Object.keys(perPlayer), ...Object.keys(anon), ...Object.keys(averages),
  ]);

  teamIds.forEach(teamId => {
    const candidates = [];

    // Mode 1: each child's best attempt, then the mean of those.
    const byPlayer = perPlayer[teamId];
    if (byPlayer) {
      const bests = Object.values(byPlayer)
        .map(rs => {
          const b = bestResult(exercise, rs);
          return b ? numeric(b.value) : null;
        })
        .filter(v => v != null);
      if (bests.length) candidates.push(fromValues(teamId, 'player', bests));
    }

    // Mode 2: every time recorded, since there is no identity to group by.
    if (anon[teamId]?.length) {
      candidates.push(fromValues(teamId, 'anon', anon[teamId].filter(v => v != null)));
    }

    // Mode 3: a weighted mean of the typed averages, by how many children each
    // covered, so an average of 3 never outweighs an average of 18.
    if (averages[teamId]?.length) {
      const rowsA = averages[teamId].filter(a => a.value != null);
      const n = rowsA.reduce((s, a) => s + a.count, 0);
      if (n > 0) {
        candidates.push({
          teamId, basis: 'average',
          mean: rowsA.reduce((s, a) => s + a.value * a.count, 0) / n,
          n,
          best: null, worst: null, spread: null,
        });
      }
    }

    if (!candidates.length) return;
    // Most children wins; richer mode breaks a tie.
    candidates.sort((a, b) => (b.n - a.n) || (BASIS_RANK[b.basis] - BASIS_RANK[a.basis]));
    out.set(teamId, candidates[0]);
  });

  return out;
}

/**
 * Roll team figures up to one age-group figure.
 *
 *   teamStats - the Map from teamStatsForExercise (or any Map/array of rows
 *               carrying { teamId, mean, n }).
 *   teamIds   - EVERY team in the age group, including the ones with no data.
 *               Passing only the teams that have data would report a partial
 *               picture as a complete one, which is the whole point of the
 *               director's "once all teams in the group have done the test".
 *
 * Returns { mean, n, teamsWithData, teamsTotal, complete, missing, bases }
 * or null when no team in the group has anything. The mean is weighted by how
 * many children each team's figure rests on, so a 6-girl team does not count
 * the same as an 18-girl one.
 */
export function ageGroupStats(teamStats, teamIds = []) {
  const map = teamStats instanceof Map
    ? teamStats
    : new Map((teamStats || []).map(r => [r.teamId, r]));

  const total = (teamIds || []).length;
  const present = (teamIds || []).map(id => map.get(id)).filter(s => s && s.mean != null && s.n > 0);
  if (!present.length) {
    return total
      ? { mean: null, n: 0, teamsWithData: 0, teamsTotal: total, complete: false, missing: [...teamIds], bases: [] }
      : null;
  }

  const n = present.reduce((s, x) => s + x.n, 0);
  const withData = new Set(present.map(x => x.teamId));

  return {
    mean: present.reduce((s, x) => s + x.mean * x.n, 0) / n,
    n,
    teamsWithData: present.length,
    teamsTotal: total,
    complete: total > 0 && present.length === total,
    missing: (teamIds || []).filter(id => !withData.has(id)),
    // Which evidence the group figure rests on, worst-first, so a screen can
    // warn that one team's typed average is holding up the whole number.
    bases: [...new Set(present.map(x => x.basis).filter(Boolean))]
      .sort((a, b) => BASIS_RANK[a] - BASIS_RANK[b]),
  };
}

/**
 * player_id -> team_id for one season, from membership rows. Later rows win,
 * which matters only for the data error of a child listed in two teams.
 */
export function teamByPlayerFrom(memberships = []) {
  const out = {};
  (memberships || []).forEach(m => {
    if (m && m.player_id && m.team_id) out[m.player_id] = m.team_id;
  });
  return out;
}

export const TEAM_AVERAGES = {
  forExercise: teamStatsForExercise,
  ageGroup: ageGroupStats,
  teamByPlayer: teamByPlayerFrom,
};
